import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import StarField from './StarField';
import { VERIFIED_STATS } from './constants';

const mix = (token, pct) => `color-mix(in srgb, var(${token}) ${pct}%, transparent)`;

// Premium hero — "Your global north star". Verified-facts-only stat band.
// Colors reference platform brand tokens (--brand-*).
export default function NrouteHero({ onRequestAccess, loginHref }) {
  const navigate = useNavigate();

  return (
    <section
      className="relative overflow-hidden pt-32 pb-20 md:pt-44 md:pb-28"
      style={{ background: 'linear-gradient(180deg, var(--brand-bg) 0%, var(--brand-primary) 100%)' }}
    >
      <StarField />

      <div className="relative mx-auto max-w-5xl px-4 text-center">
        <div className="mb-7 flex justify-center">
          <span
            className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium"
            style={{
              borderColor: mix('--brand-secondary', 30),
              backgroundColor: mix('--brand-secondary', 10),
              color: 'var(--brand-secondary)',
            }}
          >
            <Sparkles size={15} />
            AI-powered study abroad
          </span>
        </div>

        <h1 className="mx-auto max-w-4xl text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl md:text-6xl">
          Your global{' '}
          <span
            style={{
              background: 'linear-gradient(120deg, var(--brand-secondary), var(--brand-accent))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            north star
          </span>{' '}
          for studying abroad.
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-300 sm:text-xl">
          nroute pairs an AI counsellor with the world's programs — so you find the right
          course, country and path to get there. Clarity, not guesswork.
        </p>

        <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
          <button
            onClick={onRequestAccess}
            className="group inline-flex items-center justify-center gap-2 rounded-xl px-8 py-4 text-base font-semibold shadow-lg transition-all hover:brightness-105 hover:shadow-[0_0_32px_rgba(232,168,56,0.4)]"
            style={{ backgroundColor: 'var(--brand-secondary)', color: 'var(--brand-primary)' }}
          >
            Request access
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => navigate(loginHref)}
            className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/10"
          >
            Sign in
          </button>
        </div>

        <p className="mt-5 text-sm text-slate-400">Invitation-only access during rollout.</p>

        {/* Verified stat band */}
        <div className="mx-auto mt-16 grid max-w-2xl grid-cols-3 gap-4 sm:gap-6">
          {VERIFIED_STATS.map((s) => (
            <div key={s.label} className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
              <div className="text-2xl font-bold sm:text-4xl" style={{ color: 'var(--brand-secondary)' }}>
                {s.value}
              </div>
              <div className="mt-1 text-xs text-slate-300 sm:text-sm">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
